// Accueil de l'opérateur de dépôt ou d'établissement (charte D-038, artboard P05) ; l'accueil de l'inspecteur
// (P04) est celui du module des inspections (G2), ceux du transporteur (T05) et de la station sont ceux du
// module logistique (lot 3). Même logique que la supervision, sur le périmètre du rôle : ce qui demande une
// action en tête, le normal discret. Les stocks et les alertes viennent du lot 2 (fiche de stock, alertes
// ouvertes), les chargements du jour du lot 3 ; rien n'est simulé.

import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import type { InstallationFiche, InstallationListe } from '../../api/types';
import { MessageErreur, Squelette } from '../../composants/Etats';
import { dateHeure, libelleCategorie, libelleEtat, libelleProvince } from '../../composants/format';
import { Cuve } from '../../composants/Visualisations';
import { useProduits } from '../../api/requetes';
import { CuveSeuil } from '../stocks/composants';
import { useFicheStock } from '../stocks/FicheStock';
import { BlocChargementsDuJour } from '../logistique';
import { PointsAlertes, useLigneContexte, useOrganisationCourante } from './communs';

// Opérateur (dépôt, établissement prioritaire, accès externe) ------------------------------------

export function AccueilOperateur() {
  const contexte = useLigneContexte();
  const organisation = useOrganisationCourante();
  const produits = useProduits();
  const liste = useQuery({
    queryKey: ['installations', 'mes-installations'],
    queryFn: async () => (await api<InstallationListe[]>('/api/v1/installations?limite=5')).donnees,
    retry: false,
  });
  const id = liste.data?.[0]?.id;
  const fiche = useQuery({
    queryKey: ['installation', id],
    queryFn: async () => (await api<InstallationFiche>(`/api/v1/installations/${id}`)).donnees,
    enabled: Boolean(id),
  });
  const stock = useFicheStock(id);
  if (liste.isLoading || fiche.isLoading || stock.isLoading) return <Squelette lignes={6} libelle="Chargement de votre installation" />;
  const f = fiche.data;
  const s = stock.data && stock.data.physiqueLisible ? stock.data : null;
  const sansMesure = s ? s.reservoirs.filter((r) => s.reservoirsSansMesureDuJour.includes(r.id)) : [];
  const dates = s ? s.reservoirs.map((r) => r.horodatagePhysique).filter((d): d is string => Boolean(d)).sort() : [];
  const derniere = dates.length ? dates[dates.length - 1]! : null;
  const motifStock = stock.error ? 'fiche de stock non lisible avec vos habilitations' : (stock.data?.motifPhysique ?? 'aucune déclaration de stock reçue');
  const nomProduit = (code: string) => produits.data?.find((p) => p.code === code)?.nom ?? code;
  const titre = f?.nom ?? organisation.data?.nom ?? 'Mon installation';

  return (
    <div className="accueil-role">
      <div className="page-tete">
        <div>
          <span className="contexte">{contexte}</span>
          <h1>{titre}</h1>
          {f && (
            <div className="station-etat">
              <span className="attenue">
                {libelleCategorie(f.categorie)}, {f.localiteNom ?? libelleProvince(f.provinceCode)}
              </span>
              <span className="etiquette">{libelleEtat(f.etatValidation)}</span>
            </div>
          )}
        </div>
        {f && (
          <div className="actions">
            {s && (
              <Link className="bouton" to={`/stocks/installations/${f.id}`}>
                Fiche de stock
              </Link>
            )}
            <Link className="bouton discret" to={`/referentiel/installations/${f.id}`}>
              Fiche complète
            </Link>
          </div>
        )}
      </div>

      {s && s.droits.declarer && sansMesure.length > 0 && f && (
        <section className="a-faire" aria-labelledby="a-faire-operateur">
          <div>
            <span className="sur-titre">À faire maintenant</span>
            <h2 id="a-faire-operateur">
              {sansMesure.length} réservoir{sansMesure.length > 1 ? 's' : ''} sur {s.reservoirs.length} sans jaugeage aujourd’hui
            </h2>
            <p>{derniere ? `Dernier jaugeage reçu le ${dateHeure(derniere)}.` : 'Aucun jaugeage n’a encore été reçu pour votre installation.'} Sans jaugeage du jour, l’information de votre installation vieillit.</p>
          </div>
          <Link className="bouton primaire" to={`/stocks/installations/${f.id}?declarer=1`}>
            Déclarer le jaugeage
          </Link>
        </section>
      )}

      {liste.error && <MessageErreur erreur={liste.error} />}
      {!liste.error && !f && (
        <p className="message info">Aucune installation n’est lisible avec votre affectation actuelle : la fiche et les réservoirs ne peuvent pas être affichés.</p>
      )}

      <div className="deux-colonnes">
        <section className="principal carte-bloc" aria-labelledby="titre-stocks">
          <div className="carte-bloc-tete">
            <h2 id="titre-stocks">Stock par réservoir</h2>
            <span className="attenue">Dernière déclaration : {derniere ? dateHeure(derniere) : 'aucune'}</span>
          </div>
          {s && s.reservoirs.length > 0 ? (
            <div className="cuves">
              {s.reservoirs.map((r) => (
                <CuveSeuil
                  key={r.id}
                  libelle={`${r.numero}, ${nomProduit(r.produitCode)}`}
                  capacite={r.capacite}
                  unite={r.unite}
                  volume={r.physique}
                  seuil={r.nonPompable}
                  fraicheur={r.fraicheur}
                  age={r.age}
                  motifIndisponible={r.motifPhysique ?? 'aucune déclaration de stock reçue'}
                  ton={r.utilisable !== null && Number(r.utilisable) <= 0 ? 'rouge' : null}
                />
              ))}
            </div>
          ) : f?.reservoirs && f.reservoirs.length > 0 ? (
            <div className="cuves">
              {f.reservoirs.map((r) => (
                <Cuve key={r.id} produit={`${r.numero}, ${nomProduit(r.produitCode)}`} capacite={r.capaciteNominale} unite={r.unite} volume={null} fraicheur={null} motifIndisponible={motifStock} />
              ))}
            </div>
          ) : (
            <div className="bloc-insuffisant">
              <b>Stocks</b>
              <span>
                <em>indisponible</em> : {f?.reservoirsMotif ?? (f ? 'aucun réservoir enregistré' : 'aucune installation lisible')}.
              </span>
            </div>
          )}
          <div className="legende-hachure">
            <span className="echantillon" aria-hidden="true" />
            Hachuré : donnée ancienne, périmée ou absente. Ce n’est pas une gravité.
          </div>
        </section>
        <aside className="lateral">
          <BlocChargementsDuJour />
          <section className="carte-bloc" aria-labelledby="titre-alertes-operateur">
            <h2 id="titre-alertes-operateur">Alertes</h2>
            {id ? <PointsAlertes installationId={id} /> : <PointsAlertes />}
          </section>
        </aside>
      </div>
    </div>
  );
}

